import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { PublicFooter } from "@/components/legal/public-footer";
import { multiResumeBrand } from "@/utils/multiresume-brand";

import { GuideStructuredData } from "./guide-structured-data";
import {
  CHECK_AI_RESUME_EDITS_GUIDE_PATH,
  GUIDES_DIRECTORY_CANONICAL_URL,
  MANAGE_RESUME_VERSIONS_GUIDE_PATH,
  manageResumeVersionsGuideTitle,
  TAILOR_RESUME_GUIDE_PATH,
  guidesDirectoryMessages,
} from "./guides-copy";

export const manageResumeVersionsMessages = {
  title: manageResumeVersionsGuideTitle,
  metadataTitle: msg({
    id: "growth.c02.metadata-title",
    message: "How to manage multiple resume versions",
  }),
  metadataDescription: msg({
    id: "growth.c02.metadata-description",
    message:
      "Organize multiple resume versions with a master record, job-specific drafts, and saved submitted files. Includes folder names and a simple application log.",
  }),
  intro: msg({
    id: "growth.c02.intro",
    message:
      "You have `resume-final`, `resume-final-2`, and a file called `resume-updated` that might be newer than both. Uploading an application means opening each one to work out which belongs to this employer. When an interview invitation arrives a week later, you have to reconstruct what you sent.\n\nGood edits can get lost in the same pile. You explained a project clearly in one application, but can't find that paragraph for the next. An experience you cut for space disappears from every version you still use.\n\nA workable system has three separate records: your full work history, the resume you're editing for a particular job, and the exact file you submitted. You can keep them in ordinary folders with a short application log.",
  }),
  onThisPage: msg({ id: "growth.c02.on-this-page", message: "On this page" }),
  tocOne: msg({
    id: "growth.c02.toc-one",
    message: "Keep a source document and a working resume for each application",
  }),
  tocTwo: msg({ id: "growth.c02.toc-two", message: "Log the attachment you sent" }),
  tocThree: msg({ id: "growth.c02.toc-three", message: "Decide which other versions need an update" }),
  tocFour: msg({ id: "growth.c02.toc-four", message: "Archive finished applications without keeping every tiny edit" }),
  sectionOneHeading: msg({
    id: "growth.c02.section-one-heading",
    message: "1. Keep a source document and a working resume for each application",
  }),
  sectionOneOpening: msg({
    id: "growth.c02.section-one-opening",
    message:
      "Consider Alex Rivera, a fictional candidate applying for a customer marketing role at Company A and an event marketing role at Company B. The person, employers, and application records in this example are all invented.\n\nAlex keeps a document called `career-notes` with the details of past jobs and projects: dates, responsibilities, personal contributions, and results Alex has checked. It can include work that doesn't fit the current application. There's no page limit because this document is for reference, not an attachment to send an employer.\n\nEach application gets its own working resume. Editing the Company B resume leaves Company A's draft alone. If Alex needs to check what happened on a project, the career notes are still available without the cuts made for a particular role.\n\nAfter submitting to Company A, with Company B's application still in progress, the files look like this:",
  }),
  fileTree: msg({
    id: "growth.c02.file-tree",
    message:
      "Job-search/\n├── career-notes.docx\n├── applications.xlsx\n├── Company-A_Customer-Marketing/\n│   ├── job-description_2026-09-08.pdf\n│   ├── working-resume.docx\n│   └── submitted/\n│       └── Alex-Rivera_Company-A_Customer-Marketing_2026-09-10.pdf\n└── Company-B_Event-Marketing/\n    ├── job-description_2026-09-11.pdf\n    └── working-resume.docx",
  }),
  sectionOneAfterTree: msg({
    id: "growth.c02.section-one-after-tree",
    message:
      "Use the document editor you already have and change the extensions to match. The folder identifies the employer and role, so `working-resume` is a sufficient name inside it. The exported attachment will leave that folder. Give it a name that makes sense on its own, such as your name, the employer, the role, and the date. Follow an employer's naming instructions if they provide them.\n\nSave the job description you applied against, along with its source URL and the date you saved it. If the listing changes or disappears, you can still see what this resume was written for.\n\nYou don't need to reorganize years of files before your next application. Start with the roles you're pursuing now. Find their working drafts and submitted attachments, leaving older files alone for the moment. If you're unsure which file you sent, mark that as something to check. A filename containing “final” isn't proof.",
  }),
  sectionTwoHeading: msg({ id: "growth.c02.section-two-heading", message: "2. Log the attachment you sent" }),
  sectionTwoOpening: msg({
    id: "growth.c02.section-two-opening",
    message:
      "Alex's application log needs only two rows. The file locations below are relative to each employer's folder.",
  }),
  tableHeaderEmployer: msg({ id: "growth.c02.table-header-employer", message: "Employer and role" }),
  tableHeaderDescription: msg({ id: "growth.c02.table-header-description", message: "Saved job description" }),
  tableHeaderWorking: msg({ id: "growth.c02.table-header-working", message: "Working file" }),
  tableHeaderSubmitted: msg({ id: "growth.c02.table-header-submitted", message: "Submitted file" }),
  tableHeaderSent: msg({ id: "growth.c02.table-header-sent", message: "Sent" }),
  tableRowOneEmployer: msg({ id: "growth.c02.table-row-one-employer", message: "Company A, Customer Marketing" }),
  tableRowOneDescription: msg({
    id: "growth.c02.table-row-one-description",
    message: "job-description_2026-09-08.pdf",
  }),
  tableRowOneWorking: msg({ id: "growth.c02.table-row-one-working", message: "working-resume.docx" }),
  tableRowOneSubmitted: msg({
    id: "growth.c02.table-row-one-submitted",
    message: "submitted/Alex-Rivera_Company-A_Customer-Marketing_2026-09-10.pdf",
  }),
  tableRowOneSent: msg({ id: "growth.c02.table-row-one-sent", message: "2026-09-10" }),
  tableRowTwoEmployer: msg({ id: "growth.c02.table-row-two-employer", message: "Company B, Event Marketing" }),
  tableRowTwoDescription: msg({
    id: "growth.c02.table-row-two-description",
    message: "job-description_2026-09-11.pdf",
  }),
  tableRowTwoWorking: msg({ id: "growth.c02.table-row-two-working", message: "working-resume.docx" }),
  tableRowTwoSubmitted: msg({ id: "growth.c02.table-row-two-submitted", message: "Not submitted" }),
  tableRowTwoSent: msg({ id: "growth.c02.table-row-two-sent", message: "Not yet" }),
  sectionTwoAfterTable: msg({
    id: "growth.c02.section-two-after-table",
    message:
      "In your own log, make the file locations clickable so you can open the documents directly. If you have several applications underway, an “active” or “closed” column may help. Add other fields when you find yourself needing them.\n\nAt submission, export the working resume and open that attachment to check it. Upload or send the file you just checked. Once the submission succeeds, keep that same file in `submitted` and record the date. Exporting a fresh PDF later won't recover the exact file you originally sent if the draft has changed.\n\nSuppose Alex edits the Company A working resume again on September 12. The PDF sent on September 10 stays untouched. Before the interview, Alex reads that submitted copy and the saved job description, then considers whether there's anything new to explain.\n\nIf you need to send a replacement, keep both attachments and add another log entry with the time you sent the update. A timestamp in the filename can distinguish two submissions on the same day. Updating your local draft alone doesn't update the employer's copy.",
  }),
  sectionThreeHeading: msg({
    id: "growth.c02.section-three-heading",
    message: "3. Decide which other versions need an update",
  }),
  sectionThreeOpening: msg({
    id: "growth.c02.section-three-opening",
    message:
      "On September 12, Alex checks old project notes and confirms having compiled post-event survey responses, a task missing from the career notes. Alex adds the work and a reference to the record used to check it.\n\nThat update doesn't need to overwrite every resume. The Company B draft hasn't been submitted, and the survey work is relevant to its events role, so Alex adds it there. Whether it belongs in Company A's working resume is a separate choice. Company A's submitted PDF remains unchanged.\n\nAnother edit moves the event project higher in the Company B draft. That order is useful for this application; the career notes and Company A draft don't need the same rearrangement.",
  }),
  c03LinkLead: msg({
    id: "growth.c02.c03-link-lead",
    message: "For a separate check of AI-edited resume claims, see",
  }),
  c03LinkText: msg({ id: "growth.c02.c03-link-text", message: "the guide to checking AI-edited resume claims" }),
  sectionThreeQuestion: msg({
    id: "growth.c02.section-three-question",
    message: "Ask whether an edit changes what you know about your work or how you present it for this application:",
  }),
  sectionThreeListOne: msg({
    id: "growth.c02.section-three-list-one",
    message:
      "A newly confirmed responsibility, corrected employment date, or corrected result belongs in your source notes. Then check the active drafts that use the affected information.",
  }),
  sectionThreeListTwo: msg({
    id: "growth.c02.section-three-list-two",
    message:
      "A change to emphasis, order, or length belongs in the relevant working resume. You can save a useful phrasing idea for later, alongside the underlying facts, without replacing your full record with the shortened version.",
  }),
  sectionThreeListThree: msg({
    id: "growth.c02.section-three-list-three",
    message:
      "A material factual error in a submitted resume needs a correction to the employer. Keep the original attachment, send the corrected copy, and record what you sent and when.",
  }),
  sectionThreeClosing: msg({
    id: "growth.c02.section-three-closing",
    message:
      "Your resumes can emphasize different parts of your background. Employer names, actual job titles, and dates still need to agree.\n\nFor help choosing relevant projects and arranging them for a particular role, see",
  }),
  c01LinkText: msg({ id: "growth.c02.c01-link-text", message: "the guide to tailoring a resume to a job description" }),
  sectionThreeClosingSuffix: msg({
    id: "growth.c02.section-three-closing-suffix",
    message: ". Make those choices in the working resume for that application.",
  }),
  sectionFourHeading: msg({
    id: "growth.c02.section-four-heading",
    message: "4. Archive finished applications without keeping every tiny edit",
  }),
  sectionFourOpening: msg({
    id: "growth.c02.section-four-opening",
    message:
      "When Company A's process ends, Alex moves its whole folder into an archive and updates the file links in the log. Company B stays among the active applications.\n\nKeep the material that explains a completed application: the job description, the actual submitted files, and any edits or notes worth referring to again. After checking their contents, you can remove redundant copies that differ only in names such as “final” and “latest.” You don't need a separate file for every punctuation change. Decide how long to retain older applications based on whether you still need them.\n\nIf you sync these folders to cloud storage, share only the attachment an employer needs. A public link to the whole folder could expose your career notes, other applications, and private comments.\n\nFor the next application, use the log to check:",
  }),
  checklistOne: msg({
    id: "growth.c02.checklist-one",
    message: "Which employer and role does the file you're editing belong to?",
  }),
  checklistTwo: msg({
    id: "growth.c02.checklist-two",
    message: "Have you added confirmed facts or corrections to your source notes and checked affected active drafts?",
  }),
  checklistThree: msg({
    id: "growth.c02.checklist-three",
    message: "Is the attachment you're about to upload the one you opened and checked?",
  }),
  checklistFour: msg({
    id: "growth.c02.checklist-four",
    message: "After sending it, can you open that exact file from the application log?",
  }),
  sectionFourClosing: msg({
    id: "growth.c02.section-four-closing",
    message: "Create the next working resume when you're ready. Leave the earlier submitted files as they were sent.",
  }),
  productCall: msg({
    id: "growth.c02.product-call",
    message:
      "MultiResume lets you maintain a Complete Resume, create a Job Version for a role, and review changes yourself. The folders and application log in this guide are a manual method, not a claim that the product tracks submissions or backs up historical attachments automatically.",
  }),
  productHomeLink: msg({ id: "growth.c02.product-home-link", message: "Learn about MultiResume on the homepage." }),
  productAccess: msg({
    id: "growth.c02.product-access",
    message: "Access is currently invitation-only; you don't need an account to read this guide.",
  }),
} as const;

const tableHeaders = [
  manageResumeVersionsMessages.tableHeaderEmployer,
  manageResumeVersionsMessages.tableHeaderDescription,
  manageResumeVersionsMessages.tableHeaderWorking,
  manageResumeVersionsMessages.tableHeaderSubmitted,
  manageResumeVersionsMessages.tableHeaderSent,
] as const;

const tableRows = [
  [
    manageResumeVersionsMessages.tableRowOneEmployer,
    manageResumeVersionsMessages.tableRowOneDescription,
    manageResumeVersionsMessages.tableRowOneWorking,
    manageResumeVersionsMessages.tableRowOneSubmitted,
    manageResumeVersionsMessages.tableRowOneSent,
  ],
  [
    manageResumeVersionsMessages.tableRowTwoEmployer,
    manageResumeVersionsMessages.tableRowTwoDescription,
    manageResumeVersionsMessages.tableRowTwoWorking,
    manageResumeVersionsMessages.tableRowTwoSubmitted,
    manageResumeVersionsMessages.tableRowTwoSent,
  ],
] as const;

function messageText(currentI18n: typeof i18n, message: MessageDescriptor) {
  return currentI18n._(message);
}

function InlineText({ text }: { text: string }) {
  return text.split(/(`[^`]+`)/g).map((part, index) => {
    const isCode = part.startsWith("`") && part.endsWith("`");
    return isCode ? (
      <code key={`${part}-${index}`} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.95em]">
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    );
  });
}

function TextBlocks({ currentI18n, message }: { currentI18n: typeof i18n; message: MessageDescriptor }) {
  return messageText(currentI18n, message)
    .split(/\n\n+/)
    .map((paragraph, index) => (
      <p key={`${message.id}-${index}`}>
        <InlineText text={paragraph} />
      </p>
    ));
}

function LinkedTextBlocks({
  currentI18n,
  message,
  href,
  entry,
  linkText,
  suffix,
}: {
  currentI18n: typeof i18n;
  message: MessageDescriptor;
  href: string;
  entry: string;
  linkText: MessageDescriptor;
  suffix?: MessageDescriptor;
}) {
  const paragraphs = messageText(currentI18n, message).split(/\n\n+/);
  const finalParagraph = paragraphs.at(-1) ?? "";

  return (
    <>
      {paragraphs.slice(0, -1).map((paragraph, index) => (
        <p key={`${message.id}-${index}`}>
          <InlineText text={paragraph} />
        </p>
      ))}
      <p>
        <InlineText text={finalParagraph} />{" "}
        <a className="font-medium text-foreground underline underline-offset-4" href={href} data-phase6-entry={entry}>
          {messageText(currentI18n, linkText)}
        </a>
        {suffix ? messageText(currentI18n, suffix) : null}
      </p>
    </>
  );
}

function GuideSection({
  currentI18n,
  id,
  heading,
  children,
}: {
  currentI18n: typeof i18n;
  id: string;
  heading: MessageDescriptor;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 space-y-6">
      <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {messageText(currentI18n, heading)}
      </h2>
      {children}
    </section>
  );
}

function ApplicationLogTable({ currentI18n }: { currentI18n: typeof i18n }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="hidden bg-muted/30 text-xs font-semibold tracking-wide text-muted-foreground uppercase md:table-header-group">
          <tr>
            {tableHeaders.map((header) => (
              <th key={header.id} className="p-4 align-top">
                {messageText(currentI18n, header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr key={row[0].id} className="block border-t border-border first:border-t-0 md:table-row">
              {row.map((cell, index) => (
                <td key={cell.id} className="block p-4 align-top md:table-cell">
                  <span className="mb-1 block text-xs font-semibold tracking-wide text-muted-foreground uppercase md:hidden">
                    {messageText(currentI18n, tableHeaders[index])}
                  </span>
                  <span className="break-words">{messageText(currentI18n, cell)}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function getManageResumeVersionsGuideMetadata() {
  const title = messageText(i18n, manageResumeVersionsMessages.metadataTitle);
  const description = messageText(i18n, manageResumeVersionsMessages.metadataDescription);

  return {
    title: `${title} - ${multiResumeBrand.appName}`,
    description,
    ogUrl: `${GUIDES_DIRECTORY_CANONICAL_URL}/manage-resume-versions`,
  };
}

export function ManageResumeVersionsGuide() {
  const { i18n: currentI18n } = useLingui();
  const t = (message: MessageDescriptor) => messageText(currentI18n, message);

  return (
    <main id="main-content" className="min-h-svh bg-background pt-20">
      <GuideStructuredData
        headline={t(manageResumeVersionsMessages.title)}
        description={t(manageResumeVersionsMessages.metadataDescription)}
        url={`${GUIDES_DIRECTORY_CANONICAL_URL}/manage-resume-versions`}
        language={currentI18n.locale}
        directoryName={t(guidesDirectoryMessages.title)}
      />
      <article className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-12 lg:py-16">
        <nav aria-label={t(guidesDirectoryMessages.breadcrumb)} className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <li>
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href="/guides"
                data-phase6-entry="article-to-guides"
              >
                {t(guidesDirectoryMessages.title)}
              </a>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{t(manageResumeVersionsMessages.title)}</li>
          </ol>
        </nav>

        <header className="space-y-5 border-b border-border pb-10">
          <h1 className="text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl">
            {t(manageResumeVersionsMessages.title)}
          </h1>
          <div className="max-w-3xl space-y-4 text-base leading-relaxed text-muted-foreground">
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.intro} />
          </div>
        </header>

        <nav
          aria-label={t(manageResumeVersionsMessages.onThisPage)}
          className="my-10 rounded-lg border border-border bg-muted/20 p-5"
        >
          <p className="text-sm font-semibold text-foreground">{t(manageResumeVersionsMessages.onThisPage)}</p>
          <ol className="mt-3 space-y-2 text-sm">
            {[1, 2, 3, 4].map((item) => {
              const messages = [
                manageResumeVersionsMessages.tocOne,
                manageResumeVersionsMessages.tocTwo,
                manageResumeVersionsMessages.tocThree,
                manageResumeVersionsMessages.tocFour,
              ] as const;
              const ids = [
                "keep-source-and-working-resume",
                "log-the-attachment",
                "update-other-versions",
                "archive-finished-applications",
              ] as const;

              return (
                <li key={item}>
                  <a
                    className="font-medium text-foreground underline decoration-border underline-offset-4"
                    href={`#${ids[item - 1]}`}
                  >
                    {t(messages[item - 1])}
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="space-y-16 text-base leading-relaxed text-muted-foreground">
          <GuideSection
            currentI18n={currentI18n}
            id="keep-source-and-working-resume"
            heading={manageResumeVersionsMessages.sectionOneHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.sectionOneOpening} />
            <pre className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-5 text-sm leading-relaxed whitespace-pre">
              {t(manageResumeVersionsMessages.fileTree)}
            </pre>
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.sectionOneAfterTree} />
          </GuideSection>

          <GuideSection
            currentI18n={currentI18n}
            id="log-the-attachment"
            heading={manageResumeVersionsMessages.sectionTwoHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.sectionTwoOpening} />
            <ApplicationLogTable currentI18n={currentI18n} />
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.sectionTwoAfterTable} />
          </GuideSection>

          <GuideSection
            currentI18n={currentI18n}
            id="update-other-versions"
            heading={manageResumeVersionsMessages.sectionThreeHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.sectionThreeOpening} />
            <p>
              {t(manageResumeVersionsMessages.c03LinkLead)}{" "}
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href={CHECK_AI_RESUME_EDITS_GUIDE_PATH}
                data-phase6-entry="c02-to-c03"
              >
                {t(manageResumeVersionsMessages.c03LinkText)}
              </a>
            </p>
            <p>{t(manageResumeVersionsMessages.sectionThreeQuestion)}</p>
            <ul className="list-disc space-y-3 pl-5">
              {[
                manageResumeVersionsMessages.sectionThreeListOne,
                manageResumeVersionsMessages.sectionThreeListTwo,
                manageResumeVersionsMessages.sectionThreeListThree,
              ].map((item) => (
                <li key={item.id}>{t(item)}</li>
              ))}
            </ul>
            <LinkedTextBlocks
              currentI18n={currentI18n}
              message={manageResumeVersionsMessages.sectionThreeClosing}
              href={TAILOR_RESUME_GUIDE_PATH}
              entry="c02-to-c01"
              linkText={manageResumeVersionsMessages.c01LinkText}
              suffix={manageResumeVersionsMessages.sectionThreeClosingSuffix}
            />
          </GuideSection>

          <GuideSection
            currentI18n={currentI18n}
            id="archive-finished-applications"
            heading={manageResumeVersionsMessages.sectionFourHeading}
          >
            <TextBlocks currentI18n={currentI18n} message={manageResumeVersionsMessages.sectionFourOpening} />
            <ul className="list-disc space-y-3 pl-5">
              {[
                manageResumeVersionsMessages.checklistOne,
                manageResumeVersionsMessages.checklistTwo,
                manageResumeVersionsMessages.checklistThree,
                manageResumeVersionsMessages.checklistFour,
              ].map((item) => (
                <li key={item.id}>{t(item)}</li>
              ))}
            </ul>
            <p>{t(manageResumeVersionsMessages.sectionFourClosing)}</p>
            <p>
              {t(manageResumeVersionsMessages.productCall)}{" "}
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href="/"
                data-phase6-entry="article-to-home"
              >
                {t(manageResumeVersionsMessages.productHomeLink)}
              </a>{" "}
              {t(manageResumeVersionsMessages.productAccess)}
            </p>
          </GuideSection>
        </div>
      </article>

      <PublicFooter />
    </main>
  );
}

export { MANAGE_RESUME_VERSIONS_GUIDE_PATH };
